import {
  createView,
  useState,
  view,
  useRef,
  observableValue,
  useSharableState,
} from '@esui/core';
import { VSTack } from '@esui/core/layout/index.js';
import { css, html } from 'lit';
import { DemoPage, Section } from '../@demo/utils.js';
import { Button } from '../button/index.js';
import { Text } from '../text/index.js';
import { MdcTextfield, Textfield } from '../textfield/index.js';
import { Dialog, InitialFocus, MdcDialog } from './index.js';

export const [DialogDemo] = view('dialog-demo', {
  template() {
    const basicOpen = useSharableState(false);
    const actionsOpen = useSharableState(false);
    const scrollableOpen = useSharableState(false);
    const hideActionsOpen = useSharableState(false);
    const [hideActions, setHideActions] = useState(false);
    const styledOpen = useSharableState(false);
    const stackedOpen = useSharableState(false);
    const initialFocusOpen = useSharableState(false);
    const formValidationOpen = useSharableState(false);

    const textfieldRef = useRef<MdcTextfield>();
    const dialogRef = useRef<MdcDialog>();

    return DemoPage(
      Section(
        '',
        Button('Basic')
          .raised()
          .onClick(() => {
            basicOpen.value = true;
          }),

        Dialog(Text('Dialog body text'))
          .heading('Dialog header')
          .primaryAction(['ok', Button('Action 2')])
          .secondaryAction(['cancel', Button('Action 1')])
          .open(basicOpen),

        Button('Actions')
          .raised()
          .onClick(() => {
            actionsOpen.value = true;
          }),

        Dialog(
          Text(` By setting the dialogAction="my-action" attribute on any element projected into
        mwc-dialog, you can close the dialog by clicking on that element. The
        dialog will then fire a non-bubbling "closing" event and a non-bubbling
        "closed" event with an event detail of {action: "my-action"}`)
        )
          .heading('Actions')
          .primaryAction(['customAction', Button('This has action')])
          .secondaryAction(Button('This does not'))
          .open(actionsOpen),

        Button('Scrollable')
          .raised()
          .onClick(() => {
            scrollableOpen.value = true;
          }),

        Dialog(
          Text(
            `Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really long text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll. Really log text will scroll.`
          )
        )
          .heading('My Title')
          .primaryAction(['close', Button('Close this!')])
          .open(scrollableOpen),

        Button('Hide actions')
          .raised()
          .onClick(() => {
            hideActionsOpen.value = true;
          }),

        Dialog(
          VSTack(
            Text(
              `
              If you don't have actions, you may want to set the "hideActions" property.
              This property will remove extra whitespace at the bottom of this dialog.
              This button will toggle that whitespace:
              `
            ),
            Button('Toggle actions')
              .raised()
              .onClick(() => setHideActions(!hideActions)),
            Text(`hideActions property is currently: ${hideActions}`)
          ).justifyItems('start')
        )
          .heading('Hide Actions')
          .hideActions(hideActions)
          .open(hideActionsOpen),

        Button('Styled')
          .raised()
          .onClick(() => {
            styledOpen.value = true;
          }),

        Dialog(
          VSTack(
            Text('These are the current styles applied to this dialog'),
            createView(html` <pre>
--mdc-theme-surface: #FFF;
--mdc-dialog-scrim-color: rgba(35, 47, 52, .32);
--mdc-dialog-heading-ink-color: #232F34;
--mdc-dialog-content-ink-color: #232F34;
--mdc-dialog-scroll-divider-color: transparent;
--mdc-dialog-min-width: 500px;
--mdc-dialog-max-width: 700px;
--mdc-dialog-max-height: 350px;
--mdc-dialog-shape-radius: 0px;

/* color buttons */
--mdc-theme-primary: #344955;
            </pre
            >`)
          )
        )
          .heading('Styled')
          .primaryAction(['close', Button('Too stylish for me!')])
          .open(styledOpen)
          .className('styled')
          .theme({
            surfaceColor: '#fff',
            primaryColor: '#344955',
          }),

        Button('Stacked')
          .raised()
          .onClick(() => {
            stackedOpen.value = true;
          }),
        Dialog(
          Text(`This is what happens when you set the stacked property on mwc-dialog.
            Notice that the primary action is now on top.`)
        )
          .heading('Stacked')
          .stacked()
          .primaryAction(['close', Button('Primary')])
          .secondaryAction(['close', Button('Secondary')])
          .open(stackedOpen),

        Button('Initial Focus')
          .raised()
          .onClick(() => {
            initialFocusOpen.value = true;
          }),
        Dialog(
          VSTack(
            Text(`In this example we set "dialogInitialFocus" on the mwc-textfield.
            When this dialog opens, it is auto-focused.`),

            InitialFocus(Textfield('i am auto-focus', observableValue('')))
          ).justifyItems('start')
        )
          .heading('Initial Focus')
          .primaryAction(['close', Button('Primary')])
          .secondaryAction(['close', Button('Secondary')])
          .open(initialFocusOpen),

        Button('Form Validation')
          .raised()
          .onClick(() => {
            formValidationOpen.value = true;
          }),
        Dialog(
          VSTack(
            Text(`This dialog can validate user input before closing.`),

            Textfield('First name', observableValue(''))
              .required()
              .maxLength(64)
              .minLength(3)
              .ref(textfieldRef)
          ).justifyItems('start')
        )
          .primaryAction(
            Button('Confirm').onClick(() => {
              const isValid = textfieldRef.value?.checkValidity();
              if (isValid) {
                dialogRef.value?.close();
                return;
              }
              textfieldRef.value?.reportValidity();
            })
          )
          .secondaryAction(['close', Button('Cancel')])
          .open(formValidationOpen)
          .ref(dialogRef)
      )
    );
  },

  cssTemplate: css`
    .styled {
      --mdc-dialog-scrim-color: rgba(35, 47, 52, 0.32);
      --mdc-dialog-heading-ink-color: #232f34;
      --mdc-dialog-content-ink-color: #232f34;
      --mdc-dialog-scroll-divider-color: transparent;
      --mdc-dialog-min-width: 500px;
      --mdc-dialog-max-width: 700px;
      --mdc-dialog-max-height: 350px;
      --mdc-dialog-shape-radius: 0px;
    }
  `,
});
